/**
 * TB Cart Drawer — Upsell add-to-cart handling + Section-ID-Fix + Cart-Sync.
 *
 * Everything else in the drawer (open/close, quantity, remove, discount code,
 * checkout) is handled by the theme's existing global custom elements
 * (Drawer / CartDrawerComponent / DrawerCart / QuantitySelector / DiscountComponent
 * in base.js + discount.js).
 */
(function () {
    const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    /**
     * SECTION-ID-FIX (ausfallsicher)
     * Der Drawer liegt in overlay-group.json, echte ID "sections--<gruppe>__cart-drawer".
     * Das Theme fragt "?sections=cart-drawer" an -> Shopify rendert mit SCHEMA-DEFAULTS.
     *
     * Ansatz: Zusätzlich zur "cart-drawer"-Anfrage wird die echte ID mit angefragt.
     * - Liefert die echte ID gültiges Drawer-HTML -> dieses wird (normalisiert) unter
     *   "cart-drawer" eingesetzt (= Editor-Werte).
     * - Liefert sie null/Unbrauchbares -> Original "cart-drawer" bleibt unverändert.
     * Der Zusatz-Schlüssel wird vor der Übergabe an base.js immer entfernt.
     */
    const LEGACY_ID = 'cart-drawer';

    function detectRealSectionId() {
        const wrapper = document.querySelector('[id^="shopify-section-"][id$="__cart-drawer"]');
        return wrapper ? wrapper.id.replace('shopify-section-', '') : null;
    }

    const REAL_ID = detectRealSectionId();

    if (REAL_ID && REAL_ID !== LEGACY_ID && !window.__tbSectionIdFix) {
        window.__tbSectionIdFix = true;

        function splitList(value) {
            return typeof value === 'string' ? value.split(',').map((s) => s.trim()) : [];
        }
        function listHasLegacy(value) {
            return splitList(value).indexOf(LEGACY_ID) !== -1;
        }
        // "a,cart-drawer" -> "a,cart-drawer,<real>"
        function addReal(value) {
            const list = splitList(value);
            if (list.indexOf(LEGACY_ID) === -1 || list.indexOf(REAL_ID) !== -1) return value;
            list.push(REAL_ID);
            return list.join(',');
        }

        function rewriteUrl(url) {
            try {
                const u = new URL(url, window.location.origin);
                const v = u.searchParams.get('sections');
                if (!listHasLegacy(v)) return url;
                u.searchParams.set('sections', addReal(v));
                return u.toString();
            } catch (e) {
                return url;
            }
        }

        function bodyHasLegacy(body) {
            if (!body) return false;
            if (body instanceof FormData || body instanceof URLSearchParams) {
                return listHasLegacy(body.get('sections'));
            }
            if (typeof body === 'string') {
                try {
                    const d = JSON.parse(body);
                    if (Array.isArray(d.sections)) return d.sections.indexOf(LEGACY_ID) !== -1;
                    return listHasLegacy(d.sections);
                } catch (e) {
                    return false;
                }
            }
            return false;
        }

        function rewriteBody(body) {
            if (body instanceof FormData || body instanceof URLSearchParams) {
                body.set('sections', addReal(body.get('sections')));
                return body;
            }
            if (typeof body === 'string') {
                try {
                    const d = JSON.parse(body);
                    if (Array.isArray(d.sections)) {
                        if (d.sections.indexOf(REAL_ID) === -1) d.sections.push(REAL_ID);
                    } else {
                        d.sections = addReal(d.sections);
                    }
                    return JSON.stringify(d);
                } catch (e) {
                    return body;
                }
            }
            return body;
        }

        function isValidDrawerHtml(html) {
            return typeof html === 'string' && html.indexOf('<drawer-cart') !== -1;
        }

        // Ergebnis der echten ID übernehmen, falls gültig -- Zusatz-Schlüssel immer entfernen
        function translateSections(obj) {
            if (!obj || typeof obj !== 'object' || !(REAL_ID in obj)) return false;
            const realHtml = obj[REAL_ID];
            if (isValidDrawerHtml(realHtml)) {
                obj[LEGACY_ID] = realHtml.split(REAL_ID).join(LEGACY_ID);
            } else {
                console.warn('[TB] Fallback: Section "' + REAL_ID + '" nicht gerendert, nutze "' + LEGACY_ID + '" (Schema-Defaults).');
            }
            delete obj[REAL_ID];
            return true;
        }

        const fetchBeforeSectionFix = window.fetch;
        window.fetch = function (resource, options) {
            const url = typeof resource === 'string' ? resource : (resource && resource.url) || '';
            const newUrl = rewriteUrl(url);
            const urlChanged = newUrl !== url;
            const bodyChanged = !!(options && bodyHasLegacy(options.body));

            if (!urlChanged && !bodyChanged) return fetchBeforeSectionFix.apply(this, arguments);

            if (bodyChanged) options.body = rewriteBody(options.body);
            const newResource = urlChanged
                ? (typeof resource === 'string' ? newUrl : new Request(newUrl, resource))
                : resource;

            return fetchBeforeSectionFix.call(this, newResource, options).then((res) => {
                const type = res.headers.get('content-type') || '';
                if (type.indexOf('json') === -1) return res;

                return res.clone().text().then((text) => {
                    try {
                        const data = JSON.parse(text);
                        // cart/*.js -> data.sections | GET /?sections= -> oberste Ebene
                        const changed = translateSections(data && data.sections) || translateSections(data);
                        if (!changed) return res;
                        return new Response(JSON.stringify(data), {
                            status: res.status,
                            statusText: res.statusText,
                            headers: res.headers,
                        });
                    } catch (e) {
                        return res;
                    }
                });
            });
        };
    }

    /**
     * CART-SYNC (Discounty & andere Apps)
     * Discounty legt Gratis-Artikel mit einem eigenen Request NACH dem Add-to-Cart hinzu
     * bzw. entfernt sie wieder -- der Drawer ist dann schon mit altem Stand gerendert.
     *
     * - Sofort-Check: Jeder Warenkorb-Request (fetch UND XHR, also auch Discountys eigener)
     *   löst ~120 ms nach Abschluss einen Abgleich aus.
     * - Sicherheits-Checks: zusätzlich nach 0,4 / 0,9 / 1,6 / 2,8 s.
     * Weicht die Artikelanzahl vom gerenderten Drawer ab, wird er einmal neu gerendert.
     */
    const SYNC_DELAYS = [400, 900, 1600, 2800];
    const QUICK_DELAY = 120;
    let syncTimers = [];
    let quickTimer = null;
    let syncBusy = false;
    let syncQueued = false;

    function getRenderedItemCount() {
        const drawerCart = document.querySelector('drawer-cart[data-cartitemcount]');
        if (!drawerCart) return null;
        const n = parseInt(drawerCart.getAttribute('data-cartitemcount'), 10);
        return isNaN(n) ? null : n;
    }

    function checkCartSync() {
        if (syncBusy) { syncQueued = true; return; }
        if (getRenderedItemCount() === null) return;

        fetchNative(`${ROOT}cart.js`, { credentials: 'same-origin', cache: 'no-store' })
            .then((r) => r.json())
            .then((cart) => {
                if (cart.item_count === getRenderedItemCount()) return;
                syncBusy = true;
                HELPER_UTIL.dispatchCustomEvent(_EVENT_HELPER.updateAndShowCart, document, {});
                setTimeout(() => {
                    syncBusy = false;
                    if (syncQueued) { syncQueued = false; checkCartSync(); }
                }, 500);
            })
            .catch(() => {});
    }

    function scheduleCartSync() {
        clearTimeout(quickTimer);
        quickTimer = setTimeout(checkCartSync, QUICK_DELAY);
        syncTimers.forEach(clearTimeout);
        syncTimers = SYNC_DELAYS.map((d) => setTimeout(checkCartSync, d));
    }

    const CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

    // fetch ohne Sync-Hook (für die eigenen /cart.js-Abfragen)
    const fetchNative = window.fetch.bind(window);

    // Weg 1: fetch-Requests, die den Warenkorb verändern
    const fetchBeforeCartSync = window.fetch;
    window.fetch = function (resource) {
        const url = typeof resource === 'string' ? resource : (resource && resource.url) || '';
        const promise = fetchBeforeCartSync.apply(this, arguments);
        if (CART_MUTATION.test(url)) {
            promise.then(scheduleCartSync, scheduleCartSync);
        }
        return promise;
    };

    // Weg 2: XMLHttpRequest (viele Apps, u. a. Gift-Apps, nutzen noch XHR)
    if (!window.__tbXhrCartSync) {
        window.__tbXhrCartSync = true;
        const xhrOpen = XMLHttpRequest.prototype.open;
        const xhrSend = XMLHttpRequest.prototype.send;
        XMLHttpRequest.prototype.open = function (method, url) {
            this.__tbCartMutation = CART_MUTATION.test(String(url || ''));
            return xhrOpen.apply(this, arguments);
        };
        XMLHttpRequest.prototype.send = function () {
            if (this.__tbCartMutation) {
                this.addEventListener('loadend', scheduleCartSync);
            }
            return xhrSend.apply(this, arguments);
        };
    }

    // Weg 3: Theme-Events
    document.addEventListener(_EVENT_HELPER.triggerCartChange, scheduleCartSync);
    document.addEventListener(_EVENT_HELPER.OnLineItemUpdate, scheduleCartSync);

    /**
     * GIFT-RECONCILE (nur relevant bei aktiver Geschenk-Stufe im Drawer)
     * Fällt der Warenkorb unter die Geschenk-Schwelle, das Geschenk wird aber noch
     * angezeigt, wird einmal voll neu geladen.
     */
    let correctionTimer = null;
    function reconcileGiftState() {
        const cartDrawer = document.querySelector('cart-drawer-component[data-tb-gift-threshold-cents]');
        const thresholdCents = cartDrawer
            ? parseInt(cartDrawer.getAttribute('data-tb-gift-threshold-cents'), 10)
            : 0;
        const hasGiftItem = !!document.querySelector('.tb-cart-drawer__item--gift');

        if (!cartDrawer || !thresholdCents || !hasGiftItem) return;

        fetchNative(`${ROOT}cart.js`, { credentials: 'same-origin', cache: 'no-store' })
            .then((r) => r.json())
            .then((cart) => {
                const stillHasGift = !!document.querySelector('.tb-cart-drawer__item--gift');
                if (stillHasGift && cart.total_price < thresholdCents) {
                    window.location.reload();
                }
            })
            .catch(() => {});
    }
    function scheduleCorrectionRefresh() {
        clearTimeout(correctionTimer);
        correctionTimer = setTimeout(reconcileGiftState, 1200);
    }
    document.addEventListener(_EVENT_HELPER.OnLineItemUpdate, scheduleCorrectionRefresh);

    /**
     * UPSELLS
     */
    function handleUpsellAddClick(button) {
        if (button.disabled) return;
        const variantId = button.getAttribute('data-variant-id');
        if (!variantId) return;

        const originalLabel = button.textContent;
        button.disabled = true;
        button.textContent = '…';

        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');
        formData.append(
            'sections',
            HELPER_UTIL.sectionRenderUtil('cart-drawer').getSectionsString()
        );
        // Kein sections_url: Shopify lehnt manche Werte ab (z. B. "/" auf der
        // Startseite, "sections_url must be a relative path").

        HELPER_UTIL.fetchData({
            method: 'POST',
            config: {
                headers: {
                    Accept: 'application/javascript',
                    'X-Requested-With': 'XMLHttpRequest',
                },
            },
            url: `${ROOT}cart/add.js`,
            data: formData,
            onsuccess: (data) => {
                HELPER_UTIL.dispatchCustomEvent(
                    _EVENT_HELPER.triggerCartChange,
                    button,
                    { sections: data.sections }
                );
                scheduleCorrectionRefresh();
            },
            onerror: (error) => {
                button.disabled = false;
                button.textContent = originalLabel;
                if (error.json) {
                    error.json().then((errorObj) => {
                        HELPER_UTIL.dispatchCustomEvent(
                            _EVENT_HELPER.triggerToastMessage,
                            document,
                            {
                                toastType: 'common-errors',
                                messageType: 'error',
                                message:
                                    errorObj.message ||
                                    errorObj.description ||
                                    'Konnte nicht hinzugefügt werden.',
                            }
                        );
                    });
                }
            },
        });
    }

    document.addEventListener('click', (e) => {
        const button = e.target.closest('[data-tb-upsell-add]');
        if (!button) return;
        e.preventDefault();
        handleUpsellAddClick(button);
    });
})();